import React, { useState, useEffect } from 'react';
import { pacsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { printRadiologyReceipt } from '../../utils/printSlip';

const PAYMENT_METHODS = ['Cash', 'Card', 'Bank Transfer', 'Insurance', 'Waiver'];
const STATUS_COLOR = { Paid: '#16a34a', Pending: '#d97706', Waived: '#7c3aed', Partial: '#3b82f6' };
const STATUS_BG = { Paid: '#f0fdf4', Pending: '#fffbeb', Waived: '#f5f3ff', Partial: '#eff6ff' };

export default function RadiologyBilling() {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [statusFilter, setStatusFilter] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [billingModal, setBillingModal] = useState(null);
    const [billingForm, setBillingForm] = useState({ price: '', discount_percent: 0, net_amount: '', payment_status: 'Pending', payment_method: 'Cash' });
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await pacsAPI.getBillingReport({ date, payment_status: statusFilter || undefined });
            setData(res.data);
        } catch { toast.error('Failed to load billing'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [date, statusFilter]);

    const openBilling = async (order) => {
        try {
            const res = await pacsAPI.getOrderBilling(order.id);
            const d = res.data;
            const defaultPrice = d.price || d.test_price || 0;
            setBillingForm({
                price: defaultPrice,
                discount_percent: d.discount_percent || 0,
                net_amount: d.net_amount || defaultPrice,
                payment_status: d.payment_status || 'Pending',
                payment_method: d.payment_method || 'Cash',
            });
            setBillingModal({ ...order, ...d });
        } catch { toast.error('Failed to load order billing'); }
    };

    const calcNet = (price, disc) => {
        const p = parseFloat(price) || 0;
        const d = parseFloat(disc) || 0;
        return Math.round(p - (p * d / 100));
    };

    const handlePriceChange = (price) => {
        setBillingForm(f => ({ ...f, price, net_amount: calcNet(price, f.discount_percent) }));
    };
    const handleDiscChange = (disc) => {
        setBillingForm(f => ({ ...f, discount_percent: disc, net_amount: calcNet(f.price, disc) }));
    };

    const saveBilling = async () => {
        if (!billingForm.price || parseFloat(billingForm.price) <= 0) return toast.error('Price must be greater than 0');
        setSaving(true);
        try {
            await pacsAPI.saveOrderBilling({ order_id: billingModal.id, ...billingForm });
            toast.success('Billing saved');
            setLastSaved({ order: billingModal, form: { ...billingForm } });
            setBillingModal(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed');
        } finally { setSaving(false); }
    };

    const s = data?.summary;

    return (
        <div style={{ maxWidth: 1100 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>💰</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>Radiology Billing</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Manage payments for radiology orders</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13 }} />
                    <button onClick={load} style={{ padding: '7px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
                </div>
            </div>

            {/* Summary cards */}
            {s && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                    {[
                        { label: 'Total Orders', value: s.total_orders, color: '#0369a1', icon: '📋' },
                        { label: 'Collected', value: `Rs. ${Number(s.total_collected || 0).toLocaleString()}`, color: '#16a34a', icon: '✅' },
                        { label: 'Pending', value: `Rs. ${Number(s.total_pending || 0).toLocaleString()}`, color: '#d97706', icon: '⏳' },
                        { label: 'Paid Orders', value: s.paid_count, color: '#16a34a', icon: '💵' },
                        { label: 'Unpaid Orders', value: s.pending_count, color: '#dc2626', icon: '❌' },
                    ].map(c => (
                        <div key={c.label} style={{ flex: 1, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{c.icon} {c.label}</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Print receipt after save */}
            {lastSaved && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>✅ Payment saved — {lastSaved.order.order_number} · Rs. {Number(lastSaved.form.net_amount || 0).toLocaleString()}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => printRadiologyReceipt(lastSaved.order, lastSaved.form)}
                            style={{ padding: '6px 14px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            🖨️ Print Receipt
                        </button>
                        <button onClick={() => setLastSaved(null)}
                            style={{ padding: '6px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕</button>
                    </div>
                </div>
            )}

            {/* Status filter */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['', 'Pending', 'Paid', 'Partial', 'Waived'].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        style={{ padding: '5px 16px', borderRadius: 16, border: `2px solid ${statusFilter === s ? (STATUS_COLOR[s] || '#0369a1') : '#e2e8f0'}`, cursor: 'pointer', fontSize: 12, fontWeight: statusFilter === s ? 700 : 400,
                            background: statusFilter === s ? (STATUS_BG[s] || '#eff6ff') : 'white', color: statusFilter === s ? (STATUS_COLOR[s] || '#0369a1') : '#374151' }}>
                        {s || 'All'}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            ) : !data?.orders?.length ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>💰</div>
                    <p style={{ color: '#64748b' }}>No orders for this date</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Order No.', 'Patient', 'Modality / Study', 'Price', 'Discount', 'Net Amount', 'Payment', 'Method', ''].map(h => (
                                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.orders.map(o => (
                                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#0369a1' }}>{o.order_number}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{o.full_name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.mrn}</div>
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>{o.modality}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.test_name || '—'}</div>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 13 }}>{o.price ? `Rs. ${Number(o.price).toLocaleString()}` : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#d97706' }}>{o.discount_percent > 0 ? `${o.discount_percent}%` : '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700 }}>{o.net_amount ? `Rs. ${Number(o.net_amount).toLocaleString()}` : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <span style={{ background: STATUS_BG[o.payment_status] || '#f1f5f9', color: STATUS_COLOR[o.payment_status] || '#475569', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                            {o.payment_status || 'Pending'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{o.payment_method || '—'}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <button onClick={() => openBilling(o)}
                                            style={{ padding: '5px 12px', background: o.payment_status === 'Paid' ? '#f1f5f9' : '#0369a1', color: o.payment_status === 'Paid' ? '#374151' : 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                            {o.payment_status === 'Paid' ? '👁 View' : '💰 Bill'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Billing Modal */}
            {billingModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setBillingModal(null)}>
                    <div style={{ background: 'white', borderRadius: 12, width: 480, padding: 28 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0369a1', margin: 0 }}>💰 Billing — {billingModal.order_number}</h3>
                                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{billingModal.full_name} · {billingModal.modality}</p>
                            </div>
                            <button onClick={() => setBillingModal(null)} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: '4px 10px' }}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>PRICE (Rs.)</label>
                                <input type="number" value={billingForm.price}
                                    onChange={e => handlePriceChange(e.target.value)}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, boxSizing: 'border-box', fontWeight: 700 }} />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>DISCOUNT (%)</label>
                                <input type="number" min="0" max="100" value={billingForm.discount_percent}
                                    onChange={e => handleDiscChange(e.target.value)}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                        </div>

                        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 14, textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>NET AMOUNT</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: '#16a34a' }}>Rs. {Number(billingForm.net_amount || 0).toLocaleString()}</div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>PAYMENT STATUS</label>
                                <select value={billingForm.payment_status} onChange={e => setBillingForm(f => ({ ...f, payment_status: e.target.value }))}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}>
                                    {['Pending', 'Paid', 'Waived', 'Partial'].map(s => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>PAYMENT METHOD</label>
                                <select value={billingForm.payment_method} onChange={e => setBillingForm(f => ({ ...f, payment_method: e.target.value }))}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }}>
                                    {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                                </select>
                            </div>
                        </div>

                        <button onClick={saveBilling} disabled={saving}
                            style={{ width: '100%', padding: '12px', background: saving ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                            {saving ? 'Saving...' : '✅ Save Payment'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
